import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { ProcessBadge } from '../components/ProcessBadge'

interface MetaIdea {
  id: string
  title: string
  summary: string | null
  common_problem: string | null
  member_count: number
  status: string
  generated_by: string
  promoted_to_idea_id: string | null
  process_tags?: string[]
  created_at: string
  updated_at: string
}

export const Route = createFileRoute('/meta-ideas')({
  beforeLoad: async () => {
    const s = useAuth.getState()
    if (s.loading) await s.load()
    const u = useAuth.getState().user
    if (!u) throw redirect({ to: '/login' })
    if (u.role === 'user') throw redirect({ to: '/my-ideas' })
  },
  component: MetaIdeasPage,
})

interface ClusterResult {
  clusters_created: number
  ideas_grouped: number
  candidates: number
}

function MetaIdeasPage() {
  const user = useAuth((s) => s.user)
  const isAdmin = user?.role === 'admin'
  const qc = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['meta-ideas'],
    queryFn: () => api<MetaIdea[]>('/api/meta-ideas'),
  })
  const [lastResult, setLastResult] = useState<ClusterResult | null>(null)
  const [mergeMode, setMergeMode] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [mergeError, setMergeError] = useState<string | null>(null)

  const cluster = useMutation({
    mutationFn: () =>
      api<ClusterResult>(
        '/api/admin/meta-ideas/cluster?generate_summaries=false&limit=200',
        { method: 'POST' },
      ),
    onSuccess: (r) => {
      setLastResult(r)
      void qc.invalidateQueries({ queryKey: ['meta-ideas'] })
    },
  })

  const merge = useMutation({
    mutationFn: () =>
      api<MetaIdea>('/api/admin/meta-ideas/merge', {
        method: 'POST',
        body: JSON.stringify({ meta_idea_ids: Array.from(selected) }),
      }),
    onSuccess: () => {
      setSelected(new Set())
      setMergeMode(false)
      setMergeError(null)
      void qc.invalidateQueries({ queryKey: ['meta-ideas'] })
    },
    onError: () => setMergeError('Не удалось объединить мета-идеи'),
  })

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="max-w-5xl mx-auto p-8 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          Мета-идеи
        </h1>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setMergeMode((v) => !v)
                setSelected(new Set())
                setMergeError(null)
              }}
              className={`px-3 py-1.5 rounded-md text-sm border ${
                mergeMode
                  ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-400 text-amber-900 dark:text-amber-100'
                  : 'border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700'
              }`}
            >
              {mergeMode ? '✕ Выйти из режима объединения' : '🔀 Объединить'}
            </button>
            {mergeMode && selected.size >= 2 && (
              <button
                type="button"
                onClick={() => merge.mutate()}
                disabled={merge.isPending}
                className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md text-sm"
              >
                {merge.isPending
                  ? 'Объединяю…'
                  : `Объединить выбранные (${selected.size})`}
              </button>
            )}
            <button
              type="button"
              onClick={() => cluster.mutate()}
              disabled={cluster.isPending}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-md text-sm"
            >
              {cluster.isPending ? '🤖 Кластеризую…' : '🤖 Авто-кластеризация'}
            </button>
          </div>
        )}
      </div>
      {mergeError && <p className="text-sm text-red-600">{mergeError}</p>}
      {lastResult && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-md p-3 text-sm text-amber-900 dark:text-amber-100">
          Создано кластеров: <b>{lastResult.clusters_created}</b>, объединено идей:{' '}
          <b>{lastResult.ideas_grouped}</b> из {lastResult.candidates}.
        </div>
      )}
      {cluster.isError && (
        <p className="text-sm text-red-600">Ошибка кластеризации (см. логи бэкенда)</p>
      )}
      {isLoading && <p className="text-sm text-zinc-500">Загрузка…</p>}
      {error && (
        <p className="text-sm text-red-600">Не удалось загрузить мета-идеи</p>
      )}
      {data && data.length === 0 && (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-200 dark:border-zinc-700 p-8 text-center text-zinc-600 dark:text-zinc-400">
          Пока мета-идей нет. {isAdmin ? 'Нажми «Авто-кластеризация» сверху, чтобы GLM-5.1 нашёл группы похожих идей.' : 'Админ ещё не запускал кластеризацию.'}
        </div>
      )}
      {data && data.length > 0 && (
        <div className="grid grid-cols-1 gap-3">
          {data.map((m) => {
            const isSelected = selected.has(m.id)
            const inner = (
              <div className="flex items-start gap-3">
                {mergeMode && (
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(m.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1 h-4 w-4 accent-amber-600"
                    aria-label="Выбрать для объединения"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
                        {m.title}
                      </h3>
                      {m.common_problem && (
                        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2">
                          {m.common_problem}
                        </p>
                      )}
                      {m.summary && !m.common_problem && (
                        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2">
                          {m.summary}
                        </p>
                      )}
                      {m.process_tags && m.process_tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {m.process_tags.map((code) => (
                            <ProcessBadge key={code} code={code} />
                          ))}
                        </div>
                      )}
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-md bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 whitespace-nowrap">
                      {m.member_count} идей
                    </span>
                  </div>
                  <p className="mt-3 text-xs text-zinc-500">
                    {m.generated_by === 'manual'
                      ? '✋ Создано вручную'
                      : m.generated_by === 'llm'
                        ? '🤖 GLM авто-кластер'
                        : '🤖 AI кластер'}
                    {' · '}
                    {m.status === 'active' && 'активна'}
                    {m.status === 'archived' && 'архив'}
                    {m.status === 'promoted' && '✅ продвинута в инициативу'}
                  </p>
                </div>
              </div>
            )
            const cardClass = `block bg-white dark:bg-zinc-800 rounded-2xl border p-4 transition ${
              isSelected
                ? 'border-amber-500 ring-2 ring-amber-200 dark:ring-amber-800'
                : 'border-zinc-200 dark:border-zinc-700 hover:border-amber-400'
            }`
            if (mergeMode) {
              return (
                <div
                  key={m.id}
                  className={`${cardClass} cursor-pointer`}
                  onClick={() => toggleSelect(m.id)}
                >
                  {inner}
                </div>
              )
            }
            return (
              <Link
                key={m.id}
                to="/meta-ideas/$id"
                params={{ id: m.id }}
                className={cardClass}
              >
                {inner}
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
